import { useRouter } from 'next/navigation'
import { useEffect, useState } from 'react'

import paymentEvents from '@/utils/gaEvents/paymentEvents'

import type { Ticket } from '@/api/dto/ticket'
import type { ParkinglotDetail, SharedParkinglotDetail, TicketsItem } from '@/api/server/detail/dto/detailDto'
import Shared from '@/shared'
import type { Photo } from '@/shared/components/fullScreen/FullScreenImageViewer'
import useHash from '@/shared/hooks/useHash'
import type { Dispatch, SetStateAction } from 'react'

import { COUPON_TYPE_GROUP } from '@/enums/detail'

import ticketApi from '@/api/client/ticket'

type Tab = { couponSeq: string; couponName: string }

type Location = {
  parkinglotSeq: number
  parkinglotName: string
  address: string
  lat: number
  lng: number
  type: string
}

type TicketInfo = {
  parkinglotSeq: number
  parkinglotName: string
  couponName: string
  price: number
  usingLabel: string
  type: string
}

interface useViewModelProps {
  ticket: Ticket
  setTicket: Dispatch<SetStateAction<Ticket>>
  pin: ParkinglotDetail | SharedParkinglotDetail
  tickets: TicketsItem[]
}

interface ViewModel {
  texts: {
    pageTitle: string
    noticeTitle: string
    notice2Title: string
    enteringNoticeTitle: string
    recommendParkinglotsTitle: string
  }
  states: {
    tabs: Tab[]
    parkinglotPhotos: Photo[]
    ticketInfo: TicketInfo
    notice: string
    notice2: string
    enteringNotice: string
    location: Location
    isShareOpen: boolean
    isToastOpen: boolean
    isPurchase: boolean
    purchaseButtonProps: {
      text: string
      disabled: boolean
      fontSize: string
    }
    isCopyFail: boolean
    ticketSeq: number
    ticket: Ticket
  }
  setters: {
    setIsShareOpen: Dispatch<SetStateAction<boolean>>
    setIsToastOpen: Dispatch<SetStateAction<boolean>>
    setIsCopyFail: Dispatch<SetStateAction<boolean>>
  }
  handlers: {
    handleCopyAddress: () => void
    handleBackClick: () => void
    handleTabClick: (ticketSeq: string) => void
    handleClickPurchase: () => void
  }
}

const useParkingTicketDetailViewModel = ({ ticket, setTicket, tickets, pin }: useViewModelProps): ViewModel => {
  const router = useRouter()
  const { getHashAll } = useHash()
  const [parkinglotPhotos, setParkinglotPhotos] = useState<Photo[]>([])
  const [ticketInfo, setTicketInfo] = useState({
    parkinglotSeq: 0,
    parkinglotName: '',
    couponName: '',
    price: 0,
    usingLabel: '',
    type: ''
  })
  const [ticketSeq, setTicketSeq] = useState(0)
  const [notice, setNotice] = useState('')
  const [notice2, setNotice2] = useState('')
  const [enteringNotice, setEnteringNotice] = useState('')
  const [location, setLocation] = useState({
    parkinglotSeq: 0,
    parkinglotName: '',
    address: '',
    lat: 0,
    lng: 0,
    type: ''
  })
  const [tabs, setTabs] = useState<Tab[]>([])
  const [isShareOpen, setIsShareOpen] = useState(false)
  const [isToastOpen, setIsToastOpen] = useState(false)
  const [isCopyFail, setIsCopyFail] = useState(false)
  const [purchaseButtonProps, setPurchaseButtonProps] = useState({
    text: '',
    disabled: false,
    fontSize: 'body1'
  })

  const { isPurchase, setIsPurchase } = Shared.States.useIsPurchase()
  const [isAbleApp, setIsAbleApp] = useState(false)

  const pageTitle = '주차권 상세'
  const noticeTitle = '안내사항'
  const notice2Title = '결제 전 유의사항'
  const enteringNoticeTitle = '입출차 주의사항'
  const recommendParkinglotsTitle = '이 주변 주차장은 어떠세요?'

  const handleBackClick = () => {
    const hash = getHashAll()
    router.push(`/map?type=${pin.type}&id=${pin.seq}${hash}`)
  }

  const handleTabClick = async (ticketSeq: string) => {
    const pathVar = {
      couponSeq: ticketSeq
    }
    const { data } = await ticketApi.getTicketDetail(pathVar)
    setTicket(data)
  }

  const handleCopyAddress = async () => {
    try {
      await Shared.Utils.handleCopyClipBoard(pin.basic.newAddress || pin.basic.address)
      setIsToastOpen(true)
      return
    } catch (e) {
      setIsToastOpen(true)
      setIsCopyFail(true)
      return
    }
  }

  const handleClickPurchase = () => {
    if (!pin) return
    const {
      type,
      seq,
      basic: { latitude, longitude }
    } = pin

    if (!isAbleApp) {
      paymentEvents.partner.onClickPaymentButton(ticket.couponSeq, ticketInfo.parkinglotSeq)
      setIsPurchase(true)
    } else {
      const url = Shared.Utils.getMapParkinglotURL(
        {
          parkinglotType: type,
          parkinglotSeq: seq,
          lat: latitude,
          lng: longitude
        },
        'ticketdetail'
      )
      window.open(url, '_blank')
    }
  }

  useEffect(() => {
    const { isOpen, isSoldOut, nextTimeLabel, couponTypeGroup } = ticket
    const isMonthly = couponTypeGroup === COUPON_TYPE_GROUP.MONTHLY

    if (isOpen && !isSoldOut && !isMonthly) {
      setPurchaseButtonProps({
        text: '입차시간 선택하고 구매하기',
        disabled: false,
        fontSize: 'body-2'
      })
      setIsAbleApp(false)
    }

    if (isOpen && !isSoldOut && isMonthly) {
      setPurchaseButtonProps({
        text: '모두의주차장 앱에서 구매하기',
        disabled: false,
        fontSize: 'body-2'
      })
      setIsAbleApp(true)
    }

    if (isOpen && isSoldOut) {
      setPurchaseButtonProps({
        text: '현재 매진',
        disabled: true,
        fontSize: 'body-2'
      })
      setIsAbleApp(false)
    }

    if (!isOpen) {
      setPurchaseButtonProps({
        text: `판매 예정\n${nextTimeLabel}`,
        disabled: true,
        fontSize: 'caption-1'
      })
      setIsAbleApp(false)
    }
    paymentEvents.partner.paymentStart(ticket.couponSeq, ticket.parkinglot.parkinglotSeq)
  }, [ticket.couponSeq])

  useEffect(() => {
    const ticketInfo = {
      parkinglotSeq: ticket.parkinglot.parkinglotSeq,
      parkinglotName: pin.basic.name,
      couponName: ticket.couponName,
      price: ticket.price,
      usingLabel: `${ticket.usingDateLabel} ${ticket.usingTimeLabel} 사이 주차가능`,
      type: pin.type
    }

    const location = {
      parkinglotSeq: ticket.parkinglot.parkinglotSeq,
      parkinglotName: pin.basic.name,
      address: pin.basic.newAddress || pin.basic.address,
      lat: ticket.parkinglot.parkinglotLatitude,
      lng: ticket.parkinglot.parkinglotLongitude,
      type: pin.type
    }

    const tabs = tickets
      .sort((a, b) => {
        if (a.isOpen !== b.isOpen) {
          return (b.isOpen ? 1 : 0) - (a.isOpen ? 1 : 0)
        } else if (a.price !== b.price) {
          return a.price - b.price
        } else {
          return a.couponName.localeCompare(b.couponName)
        }
      })
      .map((el) => ({ couponSeq: String(el.couponSeq), couponName: el.couponName }))

    const photos = ticket.photos.map((el) => {
      return {
        fileName: el['fileName'],
        height: el['height'],
        width: el['width'],
        thumbnail: el['thumbnail'],
        pictureDesc: el['pictureDesc']
      }
    })

    setTabs(tabs)
    setTicketInfo(ticketInfo)
    setTicketSeq(ticket.couponSeq)
    setParkinglotPhotos(photos)
    setNotice(ticket.notice)
    setNotice2(ticket.notice2)
    setEnteringNotice(ticket.enteringNotice)
    setLocation(location)
  }, [ticket, tickets, pin])

  return {
    texts: {
      pageTitle,
      noticeTitle,
      notice2Title,
      enteringNoticeTitle,
      recommendParkinglotsTitle
    },
    states: {
      tabs,
      parkinglotPhotos,
      ticketInfo,
      notice,
      notice2,
      enteringNotice,
      location,
      isShareOpen,
      isToastOpen,
      isPurchase,
      purchaseButtonProps,
      isCopyFail,
      ticketSeq,
      ticket
    },
    setters: {
      setIsShareOpen,
      setIsToastOpen,
      setIsCopyFail
    },
    handlers: {
      handleCopyAddress,
      handleBackClick,
      handleTabClick,
      handleClickPurchase
    }
  }
}

export default useParkingTicketDetailViewModel
